import { chronicleIsInputAtom } from '@/atoms/chronicle';
import { Bars } from '@/components/mic-selector/Bars';
import { BarsBase } from '@/components/mic-selector/BarsBase';
import { ChronicleBars } from '@/components/mic-selector/ChronicleBars';
import { DesktopBars } from '@/components/mic-selector/DesktopBars';
import { cn } from '@/lib/cn';
import { convertSecondsToTime } from '@/models/timerModel';
import { isTauri } from '@/tauri/utils';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { useAtomValue } from 'jotai';
import { Mic, Pause, Play, Square } from 'lucide-react';
import { useIntl } from 'react-intl';

export const iconBtnClass =
  'flex size-4 items-center justify-center text-text-secondary transition-colors hover:text-text-primary';

interface IconButtonWithTooltipProps {
  readonly onClick: (e: React.MouseEvent) => void;
  readonly ariaLabel: string;
  readonly tooltipLabel: string;
  readonly children: React.ReactNode;
}

export function IconButtonWithTooltip({
  onClick,
  ariaLabel,
  tooltipLabel,
  children,
}: IconButtonWithTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button onClick={onClick} aria-label={ariaLabel} className={iconBtnClass}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <TypographyCaption>{tooltipLabel}</TypographyCaption>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}

interface VoiceIndicatorProps {
  readonly isRecording: boolean;
  readonly className?: string;
}

/** Platform-aware audio bars matching the session page visualization. */
export function VoiceIndicator({ isRecording, className }: VoiceIndicatorProps) {
  const isChronicleInput = useAtomValue(chronicleIsInputAtom);

  if (!isRecording) {
    return <BarsBase size="compact" className={className} audioReady={false} />;
  }
  if (isTauri()) {
    return isChronicleInput ? (
      <ChronicleBars size="compact" className={className} />
    ) : (
      <DesktopBars size="compact" className={className} />
    );
  }
  return <Bars isMic isTab={false} size="compact" className={className} />;
}

// Chronicle-only tile content. Row 1: voice indicator + name + timer.
// Row 2: input source ("Heidi Remote") + pause/resume + stop. Controls are
// always visible — device-initiated recordings can start/pause from any page.

interface ChronicleSessionTileContentProps {
  readonly displayName: string;
  readonly seconds: number;
  readonly isPaused: boolean;
  readonly pauseLabel: string;
  readonly resumeLabel: string;
  readonly stopLabel: string;
  readonly onPauseOrResume: (e: React.MouseEvent) => void;
  readonly onStop: (e: React.MouseEvent) => void;
  readonly className?: string;
}

export function ChronicleSessionTileContent({
  displayName,
  seconds,
  isPaused,
  pauseLabel,
  resumeLabel,
  stopLabel,
  onPauseOrResume,
  onStop,
  className,
}: ChronicleSessionTileContentProps) {
  const intl = useIntl();
  const toggleLabel = isPaused ? resumeLabel : pauseLabel;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-2">
        <VoiceIndicator isRecording={!isPaused} className="shrink-0" />
        <TypographyCaptionBold className="min-w-0 flex-1 truncate text-text-primary">
          {displayName}
        </TypographyCaptionBold>
        <TypographyCaption className="tabular-nums text-text-secondary">
          {convertSecondsToTime(seconds, true)}
        </TypographyCaption>
      </div>
      <div className="flex items-center gap-2">
        <Mic className="size-3 shrink-0 text-text-secondary" aria-hidden />
        <TypographyCaption className="min-w-0 flex-1 truncate text-text-secondary">
          {intl.formatMessage({ defaultMessage: 'Heidi Remote', id: '2LXo+tn89I' })}
        </TypographyCaption>
        <div className="flex shrink-0 items-center gap-3">
          <IconButtonWithTooltip
            onClick={onPauseOrResume}
            ariaLabel={toggleLabel}
            tooltipLabel={toggleLabel}
          >
            {isPaused ? (
              <Play className="size-4 fill-current" />
            ) : (
              <Pause className="size-4 fill-current" />
            )}
          </IconButtonWithTooltip>
          <IconButtonWithTooltip
            onClick={onStop}
            ariaLabel={stopLabel}
            tooltipLabel={stopLabel}
          >
            <Square className="size-4 fill-current" />
          </IconButtonWithTooltip>
        </div>
      </div>
    </div>
  );
}
